import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  Divider,
  Input,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import AddIcon from '~/assets/icons/FoodDetail/AddIcon'
import SubtractIcon from '~/assets/icons/FoodDetail/SubtractIcon'
import { ErrorServer } from '~/components'
import { EXTERNAL_PARTNER, STATUS_CODE } from '~/constants'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { ThemeContext } from '~/theme/ThemeProvider'
import { THEMES_NAME, variantStyles } from '~/theme/constants'
import {
  convertFoodOpitonTypesBuffet,
  generatePrimaryKey,
  transformDataDetail,
  verifyErrorTopping,
} from '~/utils/foodDetail'
import { XMarkIcon } from '../../../assets/icons'
import Loading from '../../../components/Loading'
import { loadingStates } from '../../../config/constant'
import {
  convertPrice,
  getInfoRestaurant,
  isElectronicMenu,
  isHidePlusFoodEnable,
  isLinkageFoodItemsEnable,
  isToppingImageEnable,
} from '../../../utils'
import ImageCarousel from '../components/ImageCarousel'
import NotFoundItem from '../components/NotFoundItem'
import ToppingType from '../components/ToppingType'
import styles from '../components/detailFoodStyle'
import {
  clearDataItemDetail,
  fetchItemDetail,
  selectItemDetail,
  selectTotalMoneyDetailFood,
  setFoodQuantity,
  setNoteFoodRedux,
  totalQuantityTopping,
} from '../slices/itemDetailSlice'
import { addItemToCart, addRestaurantToCart } from '~/redux/cartSlice'
import VideoDetail from '../components/VideoDetail'
import stylesCss from '../styles/DetailFood.module.scss'
import classNames from 'classnames/bind'
import PropTypes, { number, string } from 'prop-types'
import { useAppContext } from '~/contexts/AppContext'
import useIsTablet from '~/hooks/useIsTablet'
import SubtractIconTab from '~/assets/icons/FoodDetail/SubtractIconTab'
import AddIconTab from '~/assets/icons/FoodDetail/AddIconTab'
import { isPartnerSmaregiPOS } from '~/utils/externalPartner'
import ToppingImage from '../components/ToppingImage'

const cx = classNames.bind(stylesCss)
const MAX_QUANTITY_FOOD = 99
const DEFAULT_QUANTITY_FOOD = 1

// Popup chi tiết món ăn
const DetailFood = ({ foodID, isBuffetItem }) => {
  const { handleClosePopupFoodDetail } = useAppContext()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { currentThemeObject } = useContext(ThemeContext)
  const { data: originalData, loading, error, dataSelected } = useSelector(selectItemDetail)
  const { externalPartner, restaurantId, restaurantName, vatPercent, serviceRate, extraFeatures } =
    useSelector(selectRestaurant)
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi
  const isPartnerSukiya = externalPartner === EXTERNAL_PARTNER.Sukiya
  const totalMoney = useSelector(selectTotalMoneyDetailFood)
  const isNotFoundItem = error.status === STATUS_CODE.NOT_FOUND
  const isTablet = useIsTablet()
  /** convert data chi tiết món
   * buffet thì chuyển hết giá về 0
   * còn lại như cũ
   */
  const convertData = useCallback(
    (dataFood) => {
      if (isBuffetItem) {
        return {
          ...dataFood,
          food_option_types: isPartnerSmaregi
            ? dataFood.food_option_types
            : convertFoodOpitonTypesBuffet(dataFood.food_option_types), // trong trường hợp không phải smaregi thì convert giá topping về 0
          is_buffet_item: true,
          price: 0,
          original_price: dataFood?.price,
        }
      }

      return dataFood
    },
    [isBuffetItem, isPartnerSmaregi],
  )

  const data = convertData(originalData)

  const isHidePlusFoodItem = isHidePlusFoodEnable(extraFeatures) && data?.price === 0

  const {
    name,
    description,
    original_price = 0,
    discount_amount = 0,
    price,
    images,
    food_option_types: foodOptionTypes,
    max_quantity_per_order,
    stock_quantity,
    video_menu_url,
    is_using_video_menu,
    video_size,
    food_characteristics,
    buffet_items,
    serving_time,
  } = data

  // const price = is_buffet_item ? 0 : data?.price
  // const original_price = is_buffet_item ? 0 : data?.original_price

  const maxQuantityPerOrder =
    typeof max_quantity_per_order === 'number' ? max_quantity_per_order : MAX_QUANTITY_FOOD
  const stockQuantity = typeof stock_quantity === 'number' ? stock_quantity : MAX_QUANTITY_FOOD

  // state
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY_FOOD) // Số lượng món ăn đã chọn
  const [noteFood, setNoteFood] = useState('')

  // state error
  const [errorQuantity, setErrorQuantity] = useState('')
  const [errorMaxToppingAll, setErrorMaxToppingAll] = useState('')

  useEffect(() => {
    const { restaurantId } = getInfoRestaurant()
    dispatch(fetchItemDetail({ restaurantId, itemId: foodID, isBuffetItem }))
  }, [dispatch, foodID, isBuffetItem])

  useEffect(() => {
    if (typeof max_quantity_per_order === 'number' && max_quantity_per_order === 0) {
      setErrorQuantity(t('errors.errorQuantityPerOrder'))
    } else setErrorQuantity('')
  }, [max_quantity_per_order, t])

  /** Tính toán hiển thị lỗi min, max topping
   * chỉ áp dụng vs nhà hàng Smaregi và setting max_topping, min_topping (type = 'all')
   */
  const totalTopping = useSelector(totalQuantityTopping)
  useEffect(() => {
    const { max_topping, min_topping } = data
    if (
      ![EXTERNAL_PARTNER.Smaregi, EXTERNAL_PARTNER.SmaregiPOS].includes(externalPartner) ||
      typeof max_topping !== 'number' ||
      typeof min_topping !== 'number'
    )
      return
    if (max_topping < totalTopping) {
      setErrorMaxToppingAll(t('errors.warningMax', { max: max_topping }))
    } else if (min_topping > totalTopping) {
      setErrorMaxToppingAll(t('errors.warningMin', { min: min_topping }))
    } else setErrorMaxToppingAll('')
  }, [data, externalPartner, max_quantity_per_order, t, totalTopping])

  // đóng popup và clear data trong redux
  const handleClose = () => {
    handleClosePopupFoodDetail()
    dispatch(clearDataItemDetail())
  }

  const renderImages = () => {
    return <ImageCarousel images={images || []} />
  }

  // Hiển thị video trong trường hợp setting video
  const renderVideo = () => {
    return <VideoDetail video_menu_url={video_menu_url} video_size={video_size} />
  }

  /**
   * Xử lý tăng/giảm số lượng món ăn
   * Trong trường hợp số lượng món ăn > số lượng món ăn mà nhà hàng còn => Hiển thị lỗi out of stock
   * Trường hợp vượt quá số lượng / mỗi order => hiển thị lỗi
   */
  const handleChangeFoodQuantity = ({ isAdded }) => {
    if (quantity === 1 && !isAdded) return
    let error = ''

    const newQuantity = isAdded ? quantity + 1 : quantity - 1

    if (newQuantity > stockQuantity) error = t('errors.errorQuantityExceeded')

    if (newQuantity > maxQuantityPerOrder) error = t('errors.errorQuantityPerOrder')

    setErrorQuantity(error)

    setQuantity(newQuantity) // Lưu hiển thị ở UI
  }

  // lắng nghe thay đổi số lượng của món ăn => lưu vào redux
  useEffect(() => {
    dispatch(setFoodQuantity(quantity))
  }, [dispatch, quantity])

  /** func xử lí add food to cart */
  const handleAddToCart = () => {
    if (isElectronicMenu()) return

    const isErrorTopping = verifyErrorTopping({
      foodOptionTypes: data.food_option_types,
      foodOptionTypesSelected: dataSelected.food_option_types,
    })

    if (errorQuantity || isErrorTopping || errorMaxToppingAll) return

    const primaryKey = generatePrimaryKey({
      foodId: foodID,
      noteFood: isBuffetItem ? `${noteFood}-is_buffet_item` : noteFood,
      foodOptionTypes: dataSelected.food_option_types,
    })

    const newItem = transformDataDetail({
      dataSelected: convertData(dataSelected),
      noteFood,
      primaryKey,
      restaurant_id: restaurantId,
      isPartnerSmaregi,
    })

    // Mỗi lần add to cart thì lưu thêm thông tin nhà hàng để phục vụ cho việc group món ăn theo nhà hàng
    const restaurant = {
      id: restaurantId,
      name: restaurantName,
      vatPercent,
      serviceRate,
    }

    dispatch(addRestaurantToCart(restaurant))

    // Add món ăn vào giỏ hàng. Bổ sung thêm id để group món ăn theo nhà hàng
    dispatch(addItemToCart(newItem))
    handleClose()
  }
  /** cần viết riêng ra vì riêng chỗ này của theme = red_bean ngược lại với 6 theme còn lại - RED_BEAN */
  const onGetColorIconSubtract = () => {
    if (currentThemeObject.value === THEMES_NAME.red_bean) {
      return quantity > 1 ? currentThemeObject.colorText1 : currentThemeObject.colorText2
    }
    return quantity > 1 ? currentThemeObject.colorText2 : currentThemeObject.colorText1
  }
  const onGetColorIconAdd = () => {
    return currentThemeObject.value === THEMES_NAME.red_bean
      ? currentThemeObject.colorText1
      : currentThemeObject.colorText2
  }
  const onGetBackgroundBtnQuantity = () => {
    return currentThemeObject.value === THEMES_NAME.red_bean
      ? 'baseColor.colorText2'
      : 'baseColor.colorText1'
  }

  const renderBtnQuantityControl = () => {
    if (isHidePlusFoodItem) return null

    const btnQuantity = {
      fontSize: '2.4rem',
      fontWeight: 600,
      border: 'none !important',
      backgroundColor: onGetBackgroundBtnQuantity(),
    }
    const labelQuantity = {
      width: 50,
      border: 'none !important',
      fontSize: '2rem',
      fontWeight: 600,
      paddingY: '8px',
      backgroundColor: onGetBackgroundBtnQuantity(),
    }
    const btnGroupQuantity = {
      position: 'absolute',
      top: is_using_video_menu ? '16px' : '-16px',
      boxShadow: 0,
      textAlign: 'center',
      color: 'white',
      fontSize: '2.4rem',
      zIndex: 10,
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor: onGetBackgroundBtnQuantity(),
    }

    return (
      <ButtonGroup sx={btnGroupQuantity}>
        <Button sx={btnQuantity} onClick={() => handleChangeFoodQuantity({ isAdded: false })}>
          <SubtractIcon color={onGetColorIconSubtract()} size="13px" />
        </Button>
        <Box sx={labelQuantity}>
          <Box sx={{ color: 'baseColor.colorText7', fontSize: '2rem' }}>{quantity}</Box>
        </Box>

        <Button sx={btnQuantity} onClick={() => handleChangeFoodQuantity({ isAdded: true })}>
          <AddIcon color={onGetColorIconAdd()} size="13px" />
        </Button>
      </ButtonGroup>
    )
  }

  const renderBtnQuantityControlTablet = () => {
    if (isHidePlusFoodItem) return null

    if (!isTablet) return null
    const btnQuantity = {
      fontSize: '2.4rem',
      fontWeight: 600,
      border: 'none !important',
    }
    const labelQuantity = {
      width: 50,
      border: 'none !important',
      fontSize: '2rem',
      fontWeight: 600,
      paddingY: '8px',
    }
    const btnGroupQuantity = {
      top: is_using_video_menu ? '16px' : '-16px',
      boxShadow: 0,
      textAlign: 'center',
      color: 'white',
      fontSize: '2.4rem',
      zIndex: 10,
      borderRadius: '15px',
      alignItems: 'center',
    }

    return (
      <ButtonGroup sx={btnGroupQuantity}>
        <Button sx={btnQuantity} onClick={() => handleChangeFoodQuantity({ isAdded: false })}>
          <SubtractIconTab size={'4.15713rem'} />
        </Button>
        <Box sx={labelQuantity}>
          <Box sx={{ color: 'baseColor.colorText1', fontSize: '3.75rem' }}>{quantity}</Box>
        </Box>
        <Button sx={btnQuantity} onClick={() => handleChangeFoodQuantity({ isAdded: true })}>
          <AddIconTab size={'4.15713rem'} />
        </Button>
      </ButtonGroup>
    )
  }

  const renderName = () => {
    return (
      <Typography
        variant="p"
        component="p"
        sx={{
          pt: isTablet ? 0 : 2,
          pb: 1,
          mb: 0,
          fontWeight: '600',
          fontSize: isTablet ? '6.0rem' : '2.4rem',
          color: 'baseColor.colorText1',
          lineHeight: isTablet ? '100%' : 'normal',
        }}>
        {name}
      </Typography>
    )
  }

  // Hiển thị gia vị đặc trưng của món ăn
  const renderTastes = () => {
    if (!Array.isArray(food_characteristics) || food_characteristics.length <= 0) return null

    return (
      <Box pt="2px" display="flex" flexWrap="wrap" gap="8px" mb="14px">
        {food_characteristics.map((item) => {
          return (
            <Box
              key={item.id}
              flexShrink={0}
              width="calc(50% - 6px)"
              display="flex"
              alignItems="center"
              columnGap="6px">
              <img width="16" height="16" src={item.image_url} alt="taste" />
              <Box color="baseColor.colorText1" fontSize="1.2rem">
                {item.name}
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  // Nếu trong trường hợp có serving_time thì mới hiển thị
  const renderDescriptionForServingTime = () => {
    if (!serving_time || isLinkageFoodItemsEnable(extraFeatures)) return null

    return (
      <Box mb="12px" fontSize="1.8rem" color="baseColor.colorText1">
        {t('descriptions.min_left', {
          min_left: serving_time,
        })}
      </Box>
    )
  }

  const renderFoodItems = (food_items = []) => {
    return food_items.map((item) => item.related_food_item_name).join(', ')
  }

  // Hiển thị danh sách món ăn thuộc buffet
  const renderDishesForBuffet = () => {
    if (Array.isArray(buffet_items) && buffet_items.length > 0) {
      return buffet_items.map((item) => {
        return (
          <Box key={item.food_category_id} mb="12px" color="baseColor.colorText1">
            <Box fontSize="1.6rem" color="baseColor.colorText1" fontWeight="600">
              {item.food_category_name}
            </Box>
            <Box fontSize="1.6rem" color="baseColor.colorText1">
              {renderFoodItems(item.food_items)}
            </Box>
          </Box>
        )
      })
    }
  }

  // Hiển thị text max quantity / order
  const renderMaxQuantityPerOrder = () => {
    if (!typeof max_quantity_per_order === 'number' || !max_quantity_per_order) return null
    return (
      <Typography
        variant="p"
        component="p"
        color={'baseColor.colorText1'}
        sx={{ pt: 0.5, pb: 0, mb: 0, fontWeight: '400', fontSize: '1.2rem' }}>
        {t('titles.maxQuantityPerOrder')}: {max_quantity_per_order}
      </Typography>
    )
  }
  const renderDescription = () => {
    if (!description) return null

    return (
      <Typography
        variant="p"
        component="p"
        color={'baseColor.colorText1'}
        sx={{
          pb: 1,
          mb: 0,
          fontWeight: isTablet ? '300' : '400',
          fontSize: isTablet ? '3.0rem' : '1.4rem',
          marginTop: '-10px',
          fontStyle: isTablet ? 'normal' : 'italic',
          whiteSpace: 'pre-wrap',
          lineHeight: 'normal',
        }}>
        {description}
      </Typography>
    )
  }

  /**
   * Hiển thị lỗi trong trường hợp tăng giảm số lượng
   * Show lỗi vượt quá max_quantity_per_order
   * SHow lỗi khi vượt quá stock_quantity
   */
  const renderErrorMaxQuantity = () => {
    if (!errorQuantity) return null
    return (
      <Typography
        variant="p"
        component="p"
        sx={{ pt: 1, fontSize: '1.2rem', color: 'red', fontWeight: 'bold' }}>
        {errorQuantity}
      </Typography>
    )
  }

  /** Smaregi
   * lỗi này chỉ hiển thị duy nhất với nhà hàng tích hợp Smaregi
   * và cài đặt max topping, min topping (type = 'all')
   */
  const renderErrorMaxToppingFood = () => {
    const { max_topping, min_topping } = data
    if (
      ![EXTERNAL_PARTNER.Smaregi, EXTERNAL_PARTNER.SmaregiPOS].includes(externalPartner) ||
      typeof max_topping !== 'number' ||
      typeof min_topping !== 'number' ||
      !errorMaxToppingAll
    )
      return null
    return (
      <Typography
        variant="p"
        component="p"
        sx={{ pt: 1, fontSize: '1.2rem', color: 'red', fontWeight: 'bold' }}>
        {errorMaxToppingAll}
      </Typography>
    )
  }

  // Hiển thị giá gốc
  const renderOriginalPrice = () => {
    if (!discount_amount) return null

    return (
      <Box
        color="baseColor.colorText1"
        fontSize={isTablet ? '2.5rem' : '1.2rem'}
        fontWeight="400"
        sx={{ textDecoration: 'line-through' }}>
        {convertPrice(original_price)}
      </Box>
    )
  }

  // Hiển thị giá được khuyến mãi
  const renderPromotionalPrice = () => {
    return (
      <Box
        fontSize={isTablet ? '3.0rem' : '1.8rem'}
        fontWeight="600"
        lineHeight="1.2"
        color={discount_amount ? 'baseColor.colorText7' : 'baseColor.colorText1'}>
        {convertPrice(price)}
      </Box>
    )
  }

  // Hiển thị giá của món ăn
  const renderFoodPrice = () => {
    if (isHidePlusFoodItem) return null

    return (
      <Box
        mt={isTablet ? 0 : '24px'}
        display={'flex'}
        alignItems={isTablet ? 'center' : 'flex-start'}
        gap={'4px'}
        justifyContent={'flex-end'}
        flexDirection={isTablet ? 'row-reverse' : 'column'}>
        {renderOriginalPrice()}
        {renderPromotionalPrice()}
      </Box>
    )
  }

  // Hàm xử lý lấy ra tổng tiền món ăn. Nếu trong trường hợp buffet và không phải partner là smaregi thì giá luôn là 0
  const getTotalMoney = () => {
    if (isBuffetItem && !isPartnerSmaregi) return 0

    return totalMoney
  }

  /** tổng tiền món ăn bao gồm topping
   * với buffet smaregi thì chỉ hiển thị giá topping (nếu topping có giá)
   */
  const renderTotalPrice = () => {
    return (
      <Typography
        variant="p"
        sx={{ fontWeight: '600', fontSize: '2rem' }}
        color="baseColor.colorText2">
        {convertPrice(getTotalMoney())}
      </Typography>
    )
  }

  const renderAddToOrderBar = () => {
    if (isNotFoundItem) return null

    return (
      <Box position="fixed" sx={styles.appBar} maxWidth="md">
        <Divider sx={{ borderColor: currentThemeObject.colorText1 }} />
        <Toolbar sx={styles.toolbar}>
          <Button
            variant={variantStyles.btnAddToOrder}
            sx={{
              ...styles.btnAddToOrder,
              justifyContent: 'space-between',
              maxWidth: 'md',
              marginX: 'auto',
            }}
            fullWidth
            onClick={handleAddToCart}>
            <Box>
              <Typography variant="p" color="baseColor.colorText2" sx={{ fontWeight: '400' }}>
                {t('actions.addToCart')}
              </Typography>
            </Box>
            {renderTotalPrice()}
          </Button>
        </Toolbar>
      </Box>
    )
  }

  const renderAddToOrderBarTablet = () => {
    if (isNotFoundItem) return null
    return (
      <Button
        sx={{
          backgroundColor: '#FF8F1F',
          padding: '62px 30px',
          borderRadius: '20px',
          maxHeight: '158px',
          color: '#fff',
          ':hover': {
            backgroundColor: '#FF8F1F',
          },
        }}
        onClick={handleAddToCart}>
        <Box justifyContent={'center'} display={'flex'} width={'100%'}>
          <Typography
            textAlign={'center'}
            fontWeight={'700'}
            fontSize={'4.8rem'}
            lineHeight={'normal'}
            variant="p"
            color="baseColor.colorText2">
            {t('actions.addToCart')}
          </Typography>
        </Box>
      </Button>
    )
  }

  const renderIconClose = () => {
    return (
      <Typography
        onClick={handleClose}
        component="div"
        position="absolute"
        top="20px"
        left={isTablet ? 'auto' : '20px'}
        right={isTablet ? '20px' : 'auto'}
        zIndex={1}
        style={{
          ...styles.closeIcon,
          backgroundColor: isTablet ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)',
        }}>
        <XMarkIcon color={isTablet ? '#FFFFFF' : '#000'} size="12" />
      </Typography>
    )
  }

  const renderToppings = () => {
    if (!foodOptionTypes || foodOptionTypes.length <= 0) return null

    return (
      <>
        <Box
          component="div"
          className={cx('divider')}
          height="1px"
          bgcolor="baseColor.colorText1"
          mt="8px"
          mb="6px"
        />
        {foodOptionTypes.map((toppingType, index) => {
          const isLastItem = index === foodOptionTypes.length - 1

          if (isToppingImageEnable(extraFeatures) && toppingType?.display_image) {
            return <ToppingImage key={toppingType.id} {...toppingType} isLastItem={isLastItem} />
          }

          return <ToppingType key={toppingType.id} {...toppingType} isLastItem={isLastItem} />
        })}
      </>
    )
  }
  const inputChangeHandler = (e) => {
    const { value } = e.target
    setNoteFood(value)

    dispatch(setNoteFoodRedux(value || ''))
  }

  /** render input note cho món ăn */
  const renderInputNote = () => {
    if (isPartnerSukiya || isPartnerSmaregi || isPartnerSmaregiPOS()) return null

    return (
      <Input
        disableUnderline
        sx={styles.input}
        type="text"
        placeholder={t('actions.addNote')}
        onChange={inputChangeHandler}
        value={noteFood}
      />
    )
  }
  const Transition = useMemo(() => {
    return React.forwardRef(function Transition(props, ref) {
      return <Slide direction="up" ref={ref} {...props} />
    })
  }, [])

  if (loading === loadingStates.pending) {
    return <Loading />
  }

  const renderContentDialog = () => {
    if (isNotFoundItem) {
      return <NotFoundItem />
    }
    if (error.status) {
      return <ErrorServer />
    }

    return (
      <Box sx={isTablet ? styles.contentTablet : styles.content}>
        {is_using_video_menu ? renderVideo() : renderImages()}
        <Box
          sx={
            !isTablet
              ? { pl: 2, pr: 2, pt: is_using_video_menu ? '48px' : '32px', pb: '150px' }
              : styles.contentRightTab
          }
          position="relative">
          {!isElectronicMenu() && !isTablet && renderBtnQuantityControl()}
          {renderFoodPrice()}
          {renderMaxQuantityPerOrder()}
          {renderErrorMaxQuantity()}
          {renderErrorMaxToppingFood()}
          {renderName()}
          {renderDescription()}
          {renderTastes()}
          {renderDescriptionForServingTime()}
          {renderDishesForBuffet()}
          {renderToppings()}
          {!isElectronicMenu() && !isTablet && renderInputNote()}
          <Box
            display={'flex'}
            position={'sticky'}
            bottom={-1}
            backgroundColor="white"
            columnGap="63px"
            justifyContent="flex-end"
            zIndex={12}
            mt="auto">
            {!isElectronicMenu() && renderBtnQuantityControlTablet()}
            {!isElectronicMenu() && isTablet && renderAddToOrderBarTablet()}
          </Box>
        </Box>
      </Box>
    )
  }

  const customStylePaper = {
    ...styles.boxPaper,
    backgroundColor: currentThemeObject.color1,
    borderRadius: isTablet ? 20 : 0,
    bottom: isTablet ? 'auto' : 0,
    height: '863px',
    maxWidth: isTablet ? '1483px' : '100%',
  }

  return (
    <Dialog
      TransitionComponent={Transition}
      open={true}
      fullWidth
      // maxWidth="lg"
      onClose={handleClose}
      PaperProps={{ style: customStylePaper, className: cx('box_modal') }}
      aria-describedby="food-detail">
      <Box sx={isTablet ? styles.boxTablet : styles.box}>
        {renderIconClose()}
        {renderContentDialog()}
      </Box>
      {!isElectronicMenu() && !isTablet && renderAddToOrderBar()}
    </Dialog>
  )
}

DetailFood.propTypes = {
  foodID: PropTypes.oneOfType([string, number]),
  isBuffetItem: PropTypes.bool,
}

export default React.memo(DetailFood)
